import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  fromEvent,
  map,
  merge,
  skip,
  Subject,
  takeUntil,
  timer,
  Subscription,
} from 'rxjs';
import * as FAIwebviewer from '@occ/FAIwebviewer/wasm32/FAIwebviewer';

import { StateManagerProjectService } from '@state-manager/state-manager-project.service';
import {
  BoneModelType,
  CornerstoneTool,
  HotKey,
  HotKeySettings,
  OCCViewerComponentKeyActionName,
  OCCViewerComponentKeyActionNameList,
  ViewWindowIndex,
} from '@viewer/data';
import { DataRequestService } from '@viewer/services';
import { CornerstoneService } from '@viewer/cornerstone.service';
import { KeyInteractionService } from '../../interaction';
import { OCCViewerService } from './occ-viewer.service';
import { ViewerService } from '@viewer/viewer.service';
import { ConfirmDialogService } from '@shared/confirm-dialog';
import { TextsForErrorsDialogs } from '@shared/confirm-dialog/confirm-dialog.data';
import { ProjectActionsRequestService } from '@viewer/services';
import { LogMessageAction } from '@viewer/services/log-event.data';

@Component({
  selector: 'app-occ-viewer',
  templateUrl: 'occ-viewer.component.html',
  styleUrls: ['occ-viewer.component.scss'],
})
export class OCCViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() useHotKeys = true;

  private _requestForData: Subscription | null = null;
  private _componentInited: boolean = false;
  private _unsubscribe$ = new Subject<void>();
  private _lastActiveTool: CornerstoneTool = CornerstoneTool.EmptyTool;

  get is3dUpdateNeeded(): boolean {
    return this._stateManagerProjectService.is3dUpdateNeeded;
  }

  constructor(
    private _occViewerService: OCCViewerService,
    private _elementRef: ElementRef<HTMLElement>,
    private _stateManagerProjectService: StateManagerProjectService,
    private _keyInteractionService: KeyInteractionService,
    private _dataRequestService: DataRequestService,
    private _cornerstoneService: CornerstoneService,
    private _viewerService: ViewerService,
    private _confirmDialog: ConfirmDialogService,
    private _projectActionsRequestService: ProjectActionsRequestService
  ) {}

  ngOnInit(): void {
    if (this._occViewerService.OCCViewerCanvas === null) {
      this._occViewerService.OCCViewerCanvas = document.createElement('canvas');
      this._occViewerService.OCCViewerCanvas.setAttribute('id', 'Hip3dCanvas');
      this._occViewerService.OCCViewerCanvas.classList.add('occ-viewer-canvas');
      this._occViewerService.OCCViewerCanvas.tabIndex = -1;
      this._occViewerService.OCCViewerCanvas.addEventListener(
        'contextmenu',
        (event) => event.preventDefault()
      );
    }

    const viewerContent = document.getElementById('viewerContent');
    viewerContent!.appendChild(this._occViewerService.OCCViewerCanvas);

    if (this.useHotKeys) {
      this._initializeHotKeys();
    }

    this._stateManagerProjectService.current3dTool$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((currentTool) => {
        this._keyInteractionService.setActivateState<OCCViewerComponentKeyActionName>(
          'Exit3DViewTools',
          !!currentTool
        );
      });

    merge(
      merge(fromEvent(window, 'contextmenu'), fromEvent(window, 'click')).pipe(
        map((event) => {
          // It is necessary to reach parent node that contains buttons and panels on the viewer window
          return !this._elementRef.nativeElement.parentNode?.parentNode?.contains(
            event.target as HTMLElement
          );
        })
      ),
      this._stateManagerProjectService.currentSelectedWindow$.pipe(
        skip(1),
        map((index) => index !== ViewWindowIndex.Model3D)
      )
    )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((toClose) => {
        if (toClose) {
          this._occViewerService.toggleTool(null);
        }
      });

    // create viewer Object

    if (this._occViewerService.occViewer === null) {
      this._occViewerService.occViewer! = new FAIwebviewer.FAIwebviewer(
        './viewer/'
      );

      if (!this._occViewerService.occViewer!.isWasmSupported()) {
        const anElement = document.getElementById('output');
        anElement!.innerHTML +=
          'Browser is too old - WebAssembly support is missing!<br>Please check updates or install a modern browser.<br>';
      }

      this._occViewerService.occViewer.init().then(async () => {
        this._initViewer(true);
      });
    } else {
      this._initViewer(false);
    }

    this._cornerstoneService.resetChangesEmitter
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((value) => {
        if (value) {
          this.updateViewer();
        }
      });
  }

  private _initViewer(isFirstInitOfViewer: boolean = false): void {
    this._componentInited = true;

    fromEvent(window, 'resize')
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this._occViewerService.updateCanvasSize(
          this._occViewerService.occViewer!,
          this._occViewerService.OCCViewerCanvas
        );
      });

    this._occViewerService.updateCanvasSize(
      this._occViewerService.occViewer!,
      this._occViewerService.OCCViewerCanvas
    );

    this._occViewerService.init();
    this._occViewerService.initViewerBindings();

    if (isFirstInitOfViewer) {
      this._occViewerService.viewerViews = {
        d3View: new FAIwebviewer.Subview(),
      };
    }

    for (const idx in this._occViewerService.viewerViews) {
      const aView = this._occViewerService.viewerViews[idx];
      const aConfig = this._occViewerService.aViewsConfig[idx];
      aView.create(this._occViewerService.occViewer!, aConfig.size);
      aView.subviewOffset = aConfig.offset;
      aView.subviewMargins = { x: 1.0, y: 1.0 };
      aView.camera.toAllowRotation = aConfig.rotation;
      aView.camera.isPerspectiveProjection = false;
      this._occViewerService.occViewer!.viewManager.fitAllAuto(aView.id, true);
    }

    this._occViewerService.viewerViews.d3View.isActive = true;
    this._occViewerService.occViewer!.requestViewUpdate();
    this._boneModelTypeChangeSubscription();

    this._viewerService.uiGridWasChanged
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((_) => {
        if (this._occViewerService.occViewer) {
          this._occViewerService.updateCanvasSize(
            this._occViewerService.occViewer,
            this._occViewerService.OCCViewerCanvas
          );
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['viewInFullscreen']) return;
    if (this._componentInited) {
      this._occViewerService.updateCanvasSize(
        this._occViewerService.occViewer,
        this._occViewerService.OCCViewerCanvas
      );
    }
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    if (this._stateManagerProjectService.isViewerLoaded) {
      this._occViewerService.destroy();
      if (this.useHotKeys) {
        this._keyInteractionService.removeKeySettingsList([
          ...OCCViewerComponentKeyActionNameList,
        ]);
      }

      this._requestForData?.unsubscribe();
      this._requestForData = null;
    }
  }

  public async updateViewer(): Promise<void> {
    const extDisableToolState =
      this._stateManagerProjectService.isDisabledAllToolsForChangeMask;
    this._stateManagerProjectService.isDisabledAllToolsForChangeMask = true;
    this._stateManagerProjectService.is3dUpdateNeeded = false;
    this._occViewerService.isViewerLoading = true;
    if (this._stateManagerProjectService.currentTool) {
      this._lastActiveTool = this._stateManagerProjectService.currentTool;
    }

    this._cornerstoneService.toolActivate(CornerstoneTool.EmptyTool);
    const blob = await this._cornerstoneService.getUpdatedMaskBlob();
    const fileOfUpdatedMask = new File(
      [blob],
      `${this._stateManagerProjectService.caseId}_draft.nrrd`
    );

    this._dataRequestService
      .saveNrrdMask(
        this._stateManagerProjectService.caseId,
        fileOfUpdatedMask,
        false,
        true
      )
      .subscribe({
        next: () => {
          this._dataRequestService
            .generateBoneModel(
              this._stateManagerProjectService.caseId,
              false,
              true
            )
            .subscribe(() => {
              this._stateManagerProjectService.isDisabledAllToolsForChangeMask =
                !this._stateManagerProjectService.isEditAllowed
                  ? true
                  : extDisableToolState;

              this._stateManagerProjectService.boneModelType =
                BoneModelType.Temporary;
            });
            this._projectActionsRequestService
              .requestSaveLogForCase(
              this._stateManagerProjectService.caseId,
              this._stateManagerProjectService.currentUserName,
              LogMessageAction.CaseEdited,
              new Date().toISOString(),
              1,
              'UPDATE'
              )
            .subscribe();
        },
        error: () => {
          this._confirmDialog
            .openConfirmDialog(
              TextsForErrorsDialogs.PROCESSING_ERROR_PLEASE_REFRESH,
              TextsForErrorsDialogs.CLOSE,
              ''
            )
            .subscribe((_) => {
              this._stateManagerProjectService.isDisabledAllToolsForChangeMask =
                !this._stateManagerProjectService.isEditAllowed
                  ? true
                  : extDisableToolState;
              this._stateManagerProjectService.is3dUpdateNeeded = true;
              this._occViewerService.isViewerLoading = false;
              this._cornerstoneService.toolActivate(this._lastActiveTool);
            });
        },
      });
  }

  private _getData(
    boneModelType: BoneModelType,
    extDisableToolState: boolean,
    attempt: number = 1
  ): void {
    if (
      attempt >
      this._stateManagerProjectService.requestDelayData.defaultNumberOfAttempts
    ) {
      this._stateManagerProjectService.isDisabledAllToolsForChangeMask = !this
        ._stateManagerProjectService.isEditAllowed
        ? true
        : extDisableToolState;

      this._occViewerService.isViewerLoading = false;
      this._stateManagerProjectService.is3dUpdateNeeded = true;
      console.warn(
        'The model could not be updated due to problems on the server.'
      );

      return;
    }

    this._occViewerService.isViewerLoading = true;
    this._stateManagerProjectService.is3dUpdateNeeded = false;
    const requestMapper = {
      [BoneModelType.Raw]: this._dataRequestService.getBoneModel,
      [BoneModelType.Draft]: this._dataRequestService.getUpdatedBoneModel,
      [BoneModelType.Temporary]: this._dataRequestService.getTemporaryBoneModel,
    };

    if (this._requestForData !== null) {
      this._requestForData.unsubscribe();
      this._requestForData = null;
    }

    const request = requestMapper[boneModelType].bind(this._dataRequestService);
    this._requestForData = request(
      this._stateManagerProjectService.caseId,
      attempt === 1
        ? this._stateManagerProjectService.requestDelayData.bigModelRequestDelay
        : 0
    )
      .pipe(takeUntil(this._viewerService.preventAllSubscriptions$))
      .subscribe(
        async (blob) => {
          if (!blob) {
            timer(
              this._stateManagerProjectService.requestDelayData
                .modelRequestDelay
            ).subscribe((_) =>
              this._getData(boneModelType, extDisableToolState, attempt + 1)
            );

            return;
          }

          await this._loadModel(await blob.arrayBuffer());
          this._setDefaultConfig();
          this._stateManagerProjectService.isDisabledAllToolsForChangeMask =
            !this._stateManagerProjectService.isEditAllowed
              ? true
              : extDisableToolState;
          if(!this._stateManagerProjectService.isApprovedClicked && !this._stateManagerProjectService.isRejectClicked) { 
            this._cornerstoneService.toolActivate(this._lastActiveTool);
          }
          this._occViewerService.isViewerLoading = false;
          if(this._stateManagerProjectService.isViewChangeClickedReload) {
            this._stateManagerProjectService.isBoneViewLoaded = true;
            this._stateManagerProjectService.isDisabledAllToolsForChangeMask = false;
            if(this._stateManagerProjectService.isViewerLoaded) {
              this._stateManagerProjectService.isViewChangeClickedReload = false;
            }
          }

          this._occViewerService.callbackAfterLoadModel?.();
          this._occViewerService.callbackAfterLoadModel = undefined;
        },
        (_) => {
          this._occViewerService.callbackAfterLoadModel = undefined;
        }
      );
  }

  private async _loadModel(arrayBuffer: ArrayBuffer): Promise<void> {
    this._occViewerService.aViewsConfig.d3View.objects[0]?.remove();
    this._occViewerService.aViewsConfig.d3View.objects = [];
    const anObj = new FAIwebviewer.InteractiveMesh();
    await anObj.loadBufferAsync(
      this._occViewerService.occViewer!,
      arrayBuffer,
      'file.stl'
    );

    anObj.setMaterialPreset('Stone');
    anObj.materialMetallic = 0.0;
    anObj.baseColor = '#ffffff';
    for (const aConfig in this._occViewerService.aViewsConfig) {
      const clonedObj = anObj.clone();
      clonedObj.isVisible = true;
      this._occViewerService.aViewsConfig[aConfig].objects.push(clonedObj);
      clonedObj.visibleInView = this._occViewerService.viewerViews[aConfig];
    }

    this._occViewerService.occViewer!.requestViewUpdate();
  }

  private _setDefaultConfig(): void {
    const d3View = this._occViewerService.viewerViews.d3View;
    const ORIENTATION = this._occViewerService.getBoneModelOrientation();
    if (this._occViewerService.initialCameraViewpoint === null) {
      d3View.camera.setOrientation(FAIwebviewer.CameraOrientation[ORIENTATION]);
      this._occViewerService.occViewer!.viewManager.fitAllAuto(
        d3View.id,
        false
      );

      this._occViewerService.initialCameraViewpoint = d3View.camera.viewPoint;
    }

    this._occViewerService.aViewsConfig.d3View.objects.forEach(
      (obj: FAIwebviewer.InteractiveMesh) => {
        obj.materialMetallic = 0.0;
      }
    );

    d3View.camera.toAllowRotation = true;
    d3View.camera.toAllowPanning = true;
    d3View.camera.toAllowZooming = true;
    this._occViewerService.occViewer!.requestViewUpdate();
  }

  private _initializeHotKeys(): void {
    this._keyInteractionService
      .initializeHotKey(
        HotKey.Escape,
        new HotKeySettings<OCCViewerComponentKeyActionName>({
          keyActionName: 'Exit3DViewTools',
          priority: 2,
          isActive: false,
        })
      )
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this._occViewerService.toggleTool(null);
      });
  }

  private _boneModelTypeChangeSubscription(): void {
    this._stateManagerProjectService.boneModelType$
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((boneModelType) => {
        if (!boneModelType) {
          return;
        }

        const exDisableTool =
          this._stateManagerProjectService.isDisabledAllToolsForChangeMask;
        this._stateManagerProjectService.isDisabledAllToolsForChangeMask = true;
        this._getData(boneModelType, exDisableTool);
      });
  }
}
